/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import { Checkbox } from "@mui/material";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { isBitSet, getAlarmLedColor } from "../utils";

const DCU_ALARM_DEFINITIONS: AlarmDefinition[] = [
  { bit: 1, name: "Overall Alarm" },
  { bit: 2, name: "Temperature Alarm" },
  { bit: 3, name: "Power Temperature Alarm" },
  { bit: 4, name: "Digital HW Alarm" },
  { bit: 5, name: "Digital UnClock Alarm" },
  { bit: 6, name: "System Delay Alarm" },
  { bit: 7, name: "Firmware Mismatch Alarm" },
  { bit: 8, name: "Baseline Alarm" },
  { bit: 9, name: "TDD-NR Sync Alarm" },
  { bit: 10, name: "TDD-LTE Sync Alarm" },
  { bit: 11, name: "DL Overload Alarm" },
  { bit: 12, name: "DC Voltage Lower Alarm" },
];

const SFP_ALARM_ROWS: AlarmDefinition[] = [
  { bit: 1, name: "SFP Rx Alarm" },
  { bit: 2, name: "SFP Tx Alarm" },
  { bit: 3, name: "SFP Sync Alarm" },
  { bit: 4, name: "SFP Link Alarm" },
  { bit: 5, name: "SFP Temperature Alarm" },
  { bit: 6, name: "SFP Factory Alarm" },
];

const formatMetricValue = (value: unknown): string => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "N/A";
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return String(value);
  }

  if (num <= -99) {
    return "N/A";
  }

  return String(num);
};

const DcuDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
}) => {
  const dcuAlarms = parsedAttributes.dcuAlarms;
  const dcuAlarmsMask = parsedAttributes.dcuAlarmsMask;
  const dcuTopologyLocation = parsedAttributes.dcuTopologyLocation;
  const dcuEquipmentModel =  parsedAttributes.dcuEquipmentModel ?? device.model;
  const dcuEquipmentSN =    parsedAttributes.dcuEquipmentSN ?? device.serialNumber;
  const dcuTemperature = parsedAttributes.dcuEquipmentTemperature;
  const dcuFirmwareVersion = parsedAttributes.dcuFirmwareVersion;
  const dcuHardwareVersion = parsedAttributes.dcuHardwareVersion;
  const dcuSfpInfo = parsedAttributes.dcuSFPInfo || [];
  const dcuRfInfo = parsedAttributes.dcuRfInfo || [];

  const hasAlarms =
    dcuAlarms !== undefined ||
    dcuAlarmsMask !== undefined;

  const hasGeneralInfo =
    dcuTopologyLocation ||
    dcuEquipmentModel ||
    dcuEquipmentSN ||
    dcuTemperature !== undefined ||
    dcuFirmwareVersion ||
    dcuHardwareVersion;

  const isDisconnected = statusLabel === "Disconnected";

  const sfpPortMap = Array.isArray(dcuSfpInfo)
    ? dcuSfpInfo.reduce((acc, item) => {
        acc[item.dcuSFPPortNum] = item;
        return acc;
      }, {} as Record<string, any>)
    : {};

  const sfpPorts = Object.keys(sfpPortMap).sort((a, b) => {
    const aNum = Number(a);
    const bNum = Number(b);

    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
      return aNum - bNum;
    }

    return a.localeCompare(b);
  });

  const hasSfpAlarms = sfpPorts.length > 0;
  
  const rfInfoSorted = [...dcuRfInfo].sort(
    (a, b) =>
      Number(a.dcuRfInfoIndex) -
      Number(b.dcuRfInfoIndex)
  );

const hasRfInfo = rfInfoSorted.length > 0;

  const renderMetricValueWithUnit = (
    value: unknown,
    unit: string,
    valueClassName = "font-medium"
  ) => {
    const formattedValue = formatMetricValue(value);

    if (formattedValue === "N/A") {
      return <span className="font-medium text-muted-foreground">N/A</span>;
    }

    return (
      <span className={valueClassName}>
        {formattedValue} {unit}
      </span>
    );
  };



  if (alarmsOnly) {
    if (!hasAlarms) return null;

    return (
      <Card className="shadow-sm border overflow-hidden">
        <CardHeader className="px-4 py-3 border-b">
          <CardTitle className="text-sm font-semibold">
            Alarms
          </CardTitle>
        </CardHeader>

        <CardContent className="p-2">
          <AlarmTable
            alarms={dcuAlarms}
            alarmsMask={dcuAlarmsMask}
            alarmDefinitions={DCU_ALARM_DEFINITIONS}
            isDisconnected={isDisconnected}
          />
        </CardContent>
      </Card>
    );
  }

  if (!hasGeneralInfo && !hasAlarms && !hasSfpAlarms) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* DEVICE INFORMATION */}
      {hasGeneralInfo && (
        <Card className="shadow-sm border">
          <CardHeader className="px-4 py-3">
            <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">Device Information</p>
          </CardHeader>

          <CardContent className="p-4">
            <div className="space-y-2">
              {dcuTopologyLocation && (
                <DetailRow
                  label="Topology Location"
                  value={dcuTopologyLocation}
                />
              )}

              {dcuEquipmentModel && (
                <DetailRow
                  label="Model"
                  value={dcuEquipmentModel}
                />
              )}

              {dcuEquipmentSN && (
                <DetailRow
                  label="Serial Number"
                  value={dcuEquipmentSN}
                />
              )}

              {dcuTemperature !== undefined && (
                <DetailRow label="Temperature" value={`${dcuTemperature}°C`} />
              )}

              {dcuFirmwareVersion && (
                <DetailRow label="Firmware Version" value={dcuFirmwareVersion} />
              )}

              {dcuHardwareVersion && (
                <DetailRow label="Hardware Version" value={dcuHardwareVersion} />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ALARMS */}
      {hasAlarms && (
        <Card className="shadow-sm border overflow-hidden">
          <CardHeader className="px-4 py-3">
              <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">Alarms</p>
          </CardHeader>
          <CardContent className="p-2">
            <AlarmTable
              alarms={dcuAlarms}
              alarmsMask={dcuAlarmsMask}
              alarmDefinitions={DCU_ALARM_DEFINITIONS}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      {/* SFP STATUS */}
      {hasSfpAlarms && (
        <Card className="shadow-sm border">
          <CardHeader className="px-4 py-3">
              <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">SFP Status</p>
          </CardHeader>

          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {sfpPorts.map((port) => {
                const sfp = sfpPortMap[port];

                return (
                  <div
                    key={port}
                    className="rounded-xl border bg-card p-4 shadow-sm"
                  >
                    {/* HEADER */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm font-semibold">
                        Port {port}
                      </div>

                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor:
                            getAlarmLedColor(
                              isDisconnected,
                              Number(sfp?.dcuSFPAlarms) > 0
                            ),
                        }}
                      />
                    </div>

                    {/* VALUES */}
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          Temperature
                        </span>

                        {renderMetricValueWithUnit(
                          sfp?.dcuSFPTemperature,
                          "°C"
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          TX Power
                        </span>

                        {renderMetricValueWithUnit(
                          sfp?.dcuSFPTxPower,
                          "dBm"
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          RX Power
                        </span>

                        {renderMetricValueWithUnit(
                          sfp?.dcuSFPRxPower,
                          "dBm"
                        )}
                      </div>
                    </div>

                    {/* ALARM MATRIX */}
                    <div className="mt-4 border-t pt-3 space-y-2">
                      {SFP_ALARM_ROWS.map((alarm) => {
                        const isAlarmActive = isBitSet(
                          sfp?.dcuSFPAlarms,
                          alarm.bit
                        );

                        const isMaskEnabled = isBitSet(
                          sfp?.dcuSFPAlarmsMask,
                          alarm.bit
                        );

                        return (
                          <div
                            key={alarm.bit}
                            className="flex items-center justify-between gap-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <div
                                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor:
                                    getAlarmLedColor(
                                      isDisconnected,
                                      isAlarmActive
                                    ),
                                }}
                              />

                              <span className="text-[11px] truncate text-muted-foreground">
                                {alarm.name}
                              </span>
                            </div>

                            <Checkbox
                              checked={isMaskEnabled}
                              disabled
                              size="small"
                              sx={{
                                padding: 0,
                                transform: "scale(0.7)",
                                color:
                                  "var(--foreground)",
                                "&.Mui-disabled": {
                                  color:
                                    "var(--muted-foreground)",
                                },
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* RF MODULES */}
{hasRfInfo && (
  <Card className="shadow-sm border">
       <CardHeader className="px-4 py-3">
            <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">RF Modules</p>
       </CardHeader>
    <CardContent className="p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {rfInfoSorted.map((rf: any) => {
          const rfEnabled =
            rf.dcuRFSwitch === "1";

          return (
            <div
              key={rf.dcuRfInfoIndex}
              className="rounded-xl border bg-card p-4 shadow-sm"
            >
              {/* HEADER */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-semibold">
                  Module {rf.dcuRfInfoIndex}
                </div>

                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor:
                        rfEnabled
                          ? "#22c55e"
                          : "#ef4444",
                    }}
                  />

                  <span className="text-xs text-muted-foreground">
                    RF:{" "}
                    {rfEnabled
                      ? "ON"
                      : "OFF"}
                  </span>
                </div>
              </div>

              {/* DL */}
              <div className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-sky-500 font-medium">
                    ↓ DL Power
                  </span>

                  {renderMetricValueWithUnit(
                    rf.dcuDlPwrIn,
                    "dBm",
                    "font-medium text-green-600"
                  )}
                </div>

                {/* UL */}
                <div className="flex items-center justify-between">
                  <span className="text-sky-500 font-medium">
                    ↑ UL Power
                  </span>

                  {renderMetricValueWithUnit(
                    rf.dcuUlPwrOut,
                    "dBm",
                    "font-medium text-green-600"
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </CardContent>
  </Card>
)}
    </div>
  );
};

export default DcuDeviceDetails;